import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

/*
 * A stáb-panelek (Admin, Owner) fülsora — a jóváhagyott sablon `.subnav`-ja
 * (admin.html / owner.html, 2026-10-04): 14 px-es, közepes vastagságú feliratok
 * halvány ikonnal; az aktív fül világos, ikonja lila, alatta 2 px-es lila csík.
 */

const TAB_BASE =
  'relative flex-none inline-flex items-center px-3 pt-2.5 pb-3 text-sm font-medium whitespace-nowrap ' +
  'transition-colors duration-150';

const TAB_ACTIVE   = 'text-gray-50 after:absolute after:left-3 after:right-3 after:-bottom-px after:h-0.5 after:rounded-sm after:bg-brand-500';
const TAB_INACTIVE = 'text-gray-500 hover:text-gray-100';

function TabIcon({ icon, active }) {
  if (!icon) return null;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         className={`w-[15px] h-[15px] mr-[7px] flex-none ${active ? 'text-brand-500' : 'text-gray-600'}`}>
      {icon}
    </svg>
  );
}

/** Számláló a felirat után; `neutral`: puszta darabszám (pl. felhasználók), nem teendő. */
function CountBadge({ value, neutral = false }) {
  if (!value) return null;
  return (
    <span className={`ml-1.5 px-1.5 py-px rounded-[10px] font-mono text-[11px] font-medium ${
      neutral ? 'bg-gray-850 text-gray-500' : 'bg-brand-500/[0.12] text-brand-400'
    }`}>
      {typeof value === 'number' ? value.toLocaleString('en-US') : value}
    </span>
  );
}

/**
 * Több nézetet összefogó fül. A gomb felirata mindig az épp kiválasztott
 * nézetet mutatja, így egy pillantásra látszik, hol állsz — a lenyíló csak a
 * váltáshoz kell.
 */
function TabMenu({ tab, active, onChange }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const activeItem = tab.items.find(i => i.id === active);
  const isActive = !!activeItem;

  // A fülsor tördel, így ez a gomb bárhol lehet a sorban. Ha balra igazítva a
  // menü kilógna a képernyőről, átfordítjuk a jobb szélére — festés előtt,
  // hogy ne legyen villanás.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) { if (!open) setAlignRight(false); return; }
    const rect = menuRef.current.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth - 8) setAlignRight(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${TAB_BASE} ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}
      >
        <TabIcon icon={tab.icon} active={isActive} />
        {activeItem ? activeItem.label : tab.label}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
             className={`ml-1.5 w-3 h-3 flex-shrink-0 transition-transform duration-200 ${
               open ? 'rotate-180' : ''
             } ${isActive ? 'opacity-70' : 'opacity-50'}`}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className={`absolute top-full mt-1.5 z-30 min-w-[13rem] max-w-[calc(100vw-2rem)]
                      p-1 rounded-panel bg-gray-850 border border-line-strong
                      shadow-[0_20px_40px_-20px_rgba(0,0,0,0.9)] animate-scale-in ${alignRight ? 'right-0' : 'left-0'}`}
        >
          {tab.items.map(item => {
            const selected = active === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                onClick={() => { onChange(item.id); setOpen(false); }}
                className={`w-full flex items-start gap-2.5 px-3 py-2 rounded-lg text-left
                            transition-colors duration-150 ${
                  selected ? 'bg-gray-800 text-gray-50' : 'text-gray-400 hover:text-gray-50 hover:bg-white/[0.04]'
                }`}
              >
                <span className="w-4 flex-shrink-0 pt-0.5 text-brand-500">
                  {selected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                         strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{item.label}</span>
                  {item.hint && (
                    <span className="block text-xs text-gray-500 mt-0.5 leading-snug">{item.hint}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A panelek fő fülsora.
 *
 * Szándékosan TÖRDEL és nem görget: több fül a keskenyebb paneleken nem fér ki
 * egy sorba, egy vízszintesen görgethető sáv pedig némán elrejtené az utolsókat.
 *
 * Egy fül `items` tömbbel lenyílóvá válik — így a rokon nézetek (pl. rendelések
 * és előfizetés-előzmény) egy helyet foglalnak a sorban.
 */
export default function PanelTabs({ tabs, active, onChange, className = '' }) {
  return (
    <nav className={`flex flex-wrap items-end gap-0.5 border-b border-line-faint ${className}`}>
      {tabs.map(t => (
        t.items
          ? <TabMenu key={t.id} tab={t} active={active} onChange={onChange} />
          : (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              aria-current={active === t.id ? 'page' : undefined}
              className={`${TAB_BASE} ${active === t.id ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              <TabIcon icon={t.icon} active={active === t.id} />
              {t.label}
              <CountBadge value={t.count} neutral={t.countTone === 'neutral'} />
            </button>
          )
      ))}
    </nav>
  );
}

/** A stáb-panelek fejléce (a sablon `.staff-head`-je): felcímke, cím, alcím, jobbra egy címke. */
export function StaffHead({ title, subtitle, badge, eyebrow = 'Staff' }) {
  return (
    <div className="pt-10 sm:pt-12 pb-2 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="mt-2 text-[clamp(34px,4vw,44px)] font-bold leading-[1.05] tracking-[-0.045em] text-gray-50">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {badge && <span className="badge-brand">{badge}</span>}
    </div>
  );
}
